import { Platform, useWindowDimensions } from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { createContext, useContext } from "react";

/**
 * Whether the app shell's top-left slot is filled — by the back control, or by a main page's More
 * menu teleported there. Native page toolbars reserve that slot's width beside their pills.
 */
export const ToolbarLeadingSlotFilledContext = createContext(false);

/** Browse keeps its menu at the scene's left edge in both landscape orientations. */
export const ToolbarLeadingSlotAtLeftContext = createContext(false);

/**
 * The app-wide switch that hides the app shell's profile drawer button. `NavLayout` sets it once
 * for the whole app (a new learner's onboarding hands the app out one piece at a time). The
 * button hides, and native toolbar rows take its lane: they reach the window's trailing edge and
 * fade out there instead of before the button.
 */
export const ToolbarDrawerButtonHiddenContext = createContext(false);

/**
 * The same switch for the app shell's top-left slot (its back control or a page's More menu),
 * which a row gives up the same way. A filled slot still counts as filled, only not on screen.
 */
export const ToolbarLeadingSlotHiddenContext = createContext(false);

/** Match eight portrait grapheme columns; keep that control size when the device rotates. */
export function useToolbarGridMetrics() {
  const { height: windowHeight, width } = useWindowDimensions();
  const isLandscape = Platform.OS !== "web" && width > windowHeight;
  const isLeadingSlotFilled = useContext(ToolbarLeadingSlotFilledContext);
  const isLeadingSlotAtLeft = useContext(ToolbarLeadingSlotAtLeftContext);
  const isDrawerButtonHiddenInShell = useContext(ToolbarDrawerButtonHiddenContext);
  const isLeadingSlotHiddenInShell = useContext(ToolbarLeadingSlotHiddenContext);
  const insets = useSafeAreaInsets();
  const isLeadingControlRight = isLandscape && insets.right > insets.left && !isLeadingSlotAtLeft;
  const isDrawerOverRail = isLandscape && insets.left > insets.right;
  const diameter =
    Platform.OS === "web" ? 36 : Math.max(1, (Math.min(width, windowHeight) - 18 - 7 * 4) / 8);
  const hasLeadingSlot = Platform.OS !== "web" && isLeadingSlotFilled;
  // Web has no app-shell corner controls to hide: its header owns them.
  const isLeadingSlotHidden = Platform.OS !== "web" && isLeadingSlotHiddenInShell;
  const showsLeadingSlot = hasLeadingSlot && !isLeadingSlotHidden;
  // A hidden drawer frees the row's trailing edge, unless a landscape page control rests beside
  // it (the control keeps its place) or the drawer sits over the rail, out of the row anyway.
  const isTrailingEdgeFree =
    Platform.OS !== "web" &&
    isDrawerButtonHiddenInShell &&
    !isDrawerOverRail &&
    !(isLeadingControlRight && showsLeadingSlot);
  return {
    // A control left in a page's own actions lane follows the shell's slot after a 4px gap.
    actionsLeadingInset: showsLeadingSlot && !isLeadingControlRight ? diameter + 4 : 0,
    diameter,
    hasLeadingSlot,
    isLandscape,
    isLeadingControlRight,
    isLeadingSlotAtLeft,
    isLeadingSlotHidden,
    // Nothing of the shell's rests at the row's trailing edge: `trailingInset` is 0, and a row
    // keeps the 9px edge gutter inside its scroller.
    isTrailingEdgeFree,
    // The drawer and menu keep matching 9px gutters at opposite window edges.
    edgeInset: 9,
    // Each edge of the expanded tray has a 1px border and 3px vertical padding.
    expandedControlDiameter: Math.max(1, diameter - 8),
    // Keep glass highlights and shadows inside the native scene's clipping boundary.
    height: Platform.OS === "web" ? 48 : diameter + 18,
    // Reserve 9px between the leading edge control (the top-left slot, or web's More) and the
    // adjacent pill.
    leadingInset: Platform.OS === "web" ? 51 : isLeadingControlRight ? 9 : 9 + diameter + 9,
    trailingInset:
      isTrailingEdgeFree || Platform.OS === "web"
        ? 0
        : isDrawerOverRail
          ? 9
          : 9 + diameter + 9 + (isLeadingControlRight && showsLeadingSlot ? diameter + 9 : 0),
  };
}
